// The army builder: three picks (anchor, a block of three core units, striker) from the options the
// league drew for you, then a name for the army and nicknames for its units. The server builds the
// bots' armies.
import React from 'react';
import type { LeagueView } from '../../leagues/api';
import { playerName } from '../../leagues/api';
import type { Act } from './LeaguePage';
import { offers, buildArmy, botArmy, teamArmy, teamPicks, teamName, TEAM_KEYS, PICKS, SLOT_ROLES, POOLS } from '../../leagues/armyBuilder';
import { ZoneHexes, traitList } from './parts';
import { unitCost } from '../../games/capture-the-flag/units';
import { ArmyTable } from './ArmyTable';

const ROLE_TEXT: Record<string, string> = {
  Anchor: 'Anchors hold ground: big zones, hard to tag, usually no flag.',
  Core: 'Your three core units are all the same: they claim, assist and carry the flag.',
  Striker: 'Strikers are fast or reach far: they take the flag or pick off carriers.',
};
const NICK_HINTS = ['e.g. The Wall', 'e.g. Pip', 'e.g. Squeak', 'e.g. Biscuit', 'e.g. Flash'];
const unitOf = (name: string) => [...POOLS.Anchor, ...POOLS.Core, ...POOLS.Striker].find(u => u.name === name);

export default function LeagueBuild({ league: L, act }: { league: LeagueView; act: Act }) {
  const me = L.me ? L.players.find(p => p.id === L.me!.id) : undefined;
  // Picks so far are kept in this browser, so a reload doesn't lose them.
  const draftKey = me ? `league-build-${L.id}-${me.id}` : '';
  const saved = React.useMemo(() => { try { return JSON.parse(localStorage.getItem(draftKey) ?? 'null'); } catch { return null; } }, [draftKey]);
  const [picks, setPicks] = React.useState<string[]>(saved?.picks ?? []);
  const [armyName, setArmyName] = React.useState<string>(saved?.armyName ?? (me ? `${me.name}'s army` : ''));
  const [nicks, setNicks] = React.useState<string[]>(saved?.nicks ?? ['', '', '', '', '']);
  React.useEffect(() => {
    if (!draftKey) return;
    try { localStorage.setItem(draftKey, JSON.stringify({ picks, armyName, nicks })); } catch { /* storage blocked */ }
  }, [draftKey, picks, armyName, nicks]);
  const [saving, setSaving] = React.useState(false);

  const n = picks.length;
  const building = me && !me.armyBuilt;
  // "Each picks a team": choosing a built-in team fills in all three picks; naming works as in the builder.
  const pickMode = L.settings.armies === 'pick';
  const chooseTeam = (key: string) => { setPicks(teamPicks(key)); if (!armyName.trim() || armyName === `${me?.name}'s army`) setArmyName(teamName(key)); };
  const slots: { role: string; name: string | undefined }[] = SLOT_ROLES.map((role, i) => ({ role, name: i === 0 ? picks[0] : i < 4 ? picks[1] : picks[2] }));
  const current = n === 0 ? [0] : n === 1 ? [1, 2, 3] : n === 2 ? [4] : [];
  if (L.settings.armies === 'pick') current.length = 0;   // a whole team is picked at once
  const total = slots.reduce((a, s) => a + (s.name ? unitCost(unitOf(s.name)!) : 0), 0);

  const save = async () => {
    setSaving(true);
    const done = await act('army', { army: buildArmy(armyName.trim() || `${me!.name}'s army`, picks, nicks) });
    if (!done) setSaving(false);
    else try { localStorage.removeItem(draftKey); } catch { /* storage blocked */ }
  };

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'flex-start' }}>
      <section style={{ flex: '999 1 600px', minWidth: 0, display: 'grid', gap: 16 }}>
        <div>
          <p className="site-kicker">{L.name} · {pickMode ? 'pick a team' : 'army builder'}</p>
          <h1 className="site-display" style={{ fontSize: 40, lineHeight: 1.05, marginTop: 2 }}>
            {!building ? (me ? 'Your army is ready' : pickMode ? 'Everyone is picking a team' : 'Everyone is building armies') : n < 3 ? (pickMode ? 'Pick your team' : `Pick ${n + 1} of 3: your ${PICKS[n].toLowerCase()}`) : 'Name your army'}
          </h1>
          <p style={{ color: '#c9c7d3', marginTop: 4 }}>
            {!building ? 'The rounds appear when every army is built.' : n < 3 && pickMode ? "One of the game's built-in teams: an anchor, three core units and a striker. Others can pick the same one." : n < 3 ? ROLE_TEXT[PICKS[n]] : 'Five units: an anchor, three core units and a striker. Nicknames show in the game when you select a unit, and in the log.'}
          </p>
        </div>

        {me?.armyBuilt && me.army && (
          <section className="site-panel" style={{ display: 'grid', gap: 10, maxWidth: 820 }}>
            <h2 className="site-display" style={{ fontSize: 26, textTransform: 'none' }}>{me.army.name}</h2>
            {me.army.builtBy && me.army.builtBy !== me.id && <p style={{ color: '#f2d48f' }}>{playerName(L, me.army.builtBy)} built this army for you.</p>}
            <ArmyTable player={me} />
          </section>
        )}

        {building && n < 3 && pickMode && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))', gap: 16 }}>
            {TEAM_KEYS.map(key => {
              const units = teamPicks(key).map(unitOf);
              const squad = [units[0], units[1], units[1], units[1], units[2]];
              return (
                <article key={key} className="site-panel" style={{ padding: 16, display: 'grid', gap: 10, alignContent: 'start' }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
                    <h2 className="site-display" style={{ fontSize: 24, textTransform: 'none' }}>{teamName(key)}</h2>
                    <span className="site-muted" style={{ fontSize: 14 }}>{squad.reduce((a, u) => a + (u ? unitCost(u) : 0), 0)} pts</span>
                  </div>
                  <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 6 }}>
                    {(['Anchor', 'Core', 'Striker'] as const).map((role, i) => {
                      const u = units[i];
                      if (!u) return null;
                      const traits = traitList(u);
                      return (
                        <li key={role} style={{ fontSize: 14 }}>
                          <span className="site-kicker" style={{ fontSize: 11 }}>{role}</span>{' '}
                          <strong>{role === 'Core' ? `3 × ${u.name}` : u.name}</strong>{' '}
                          <span className="site-muted">· Move {u.move} · {u.zone.length}-hex zone{traits.length ? ` · ${traits.map(t => t.name).join(', ')}` : ''}</span>
                        </li>
                      );
                    })}
                  </ul>
                  <button type="button" className="site-btn site-btn-primary" onClick={() => chooseTeam(key)}>Pick {teamName(key)}</button>
                </article>
              );
            })}
          </div>
        )}

        {building && n < 3 && !pickMode && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
              {offers(L.seed, me!.id, n, L.settings.choices).map(u => {
                const traits = traitList(u);
                const core = PICKS[n] === 'Core';
                return (
                  <article key={u.name} className="site-panel" style={{ padding: 16, display: 'grid', gap: 10, alignContent: 'start' }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
                      <h2 className="site-display" style={{ fontSize: 28 }}>{u.name}</h2>
                      <span className="site-muted" style={{ fontSize: 14 }}>{core ? '3 × ' : ''}{unitCost(u)} pts</span>
                    </div>
                    <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                      <ZoneHexes unit={u} />
                      <div style={{ display: 'grid', gap: 2 }}>
                        <span className="site-kicker">Move</span>
                        <span className="site-display" style={{ fontSize: 40, lineHeight: 1 }}>{u.move}</span>
                        <span className="site-muted" style={{ fontSize: 13 }}>{u.zone.length}-hex zone{core ? ' · you get three' : ''}</span>
                      </div>
                    </div>
                    <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 4, minHeight: 40 }}>
                      {traits.length ? traits.map(t => (
                        <li key={t.name} style={{ fontSize: 14 }}><strong style={{ color: t.good ? 'var(--ok)' : 'var(--red)' }}>{t.name}</strong> <span style={{ color: '#c9c7d3' }}>{t.text}</span></li>
                      )) : <li className="site-muted" style={{ fontSize: 14 }}>No special traits: its Move and zone are what it has.</li>}
                    </ul>
                    <button type="button" className="site-btn site-btn-primary" style={{ minHeight: 46, fontFamily: 'var(--display)', fontSize: 20, textTransform: 'uppercase', letterSpacing: '.04em' }}
                      onClick={() => setPicks([...picks, u.name])}>{core ? `Pick ${u.name} ×3` : `Pick ${u.name}`}</button>
                  </article>
                );
              })}
            </div>
            <p className="site-muted" style={{ fontSize: 14 }}>{L.settings.choices} options a pick, drawn for you by the league. There's no budget: points are only a rough guide to strength (Move, the zone's size and shape, and traits), so pick what you like the look of. ▲ marks the unit and the way it faces.{n > 0 && <> <button type="button" className="site-btn site-btn-small" style={{ minHeight: 30, marginLeft: 6 }} onClick={() => setPicks(picks.slice(0, -1))}>Undo last pick</button></>}</p>
          </>
        )}

        {building && n === 3 && (
          <form className="site-panel" style={{ display: 'grid', gap: 12, maxWidth: 640 }} onSubmit={e => { e.preventDefault(); save(); }}>
            <label style={{ display: 'grid', gap: 6 }}><span className="site-kicker">Army name</span>
              <input value={armyName} maxLength={40} onChange={e => setArmyName(e.target.value)} style={{ minHeight: 48, fontSize: 17 }} />
            </label>
            <span className="site-kicker" style={{ marginTop: 6 }}>Nicknames (optional)</span>
            {slots.map((s, i) => (
              <label key={i} style={{ display: 'grid', gridTemplateColumns: '150px minmax(0, 1fr)', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 14 }}><span className="site-kicker" style={{ fontSize: 11 }}>{s.role}</span><br /><strong>{s.name}</strong></span>
                <input value={nicks[i]} maxLength={24} placeholder={NICK_HINTS[i]} onChange={e => { const v = nicks.slice(); v[i] = e.target.value; setNicks(v); }} />
              </label>
            ))}
            <p className="site-muted site-small">Once saved, these are your units for the whole league{L.settings.improve ? '; they gain improvements after each game' : ''}.</p>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button type="submit" className="site-btn site-btn-primary site-btn-big" disabled={saving}>Save my army</button>
              {pickMode
                ? <button type="button" className="site-btn" style={{ minHeight: 56 }} onClick={() => setPicks([])}>Pick another team</button>
                : <button type="button" className="site-btn" style={{ minHeight: 56 }} onClick={() => setPicks(picks.slice(0, 2))}>Change the striker</button>}
            </div>
          </form>
        )}
      </section>

      <aside style={{ flex: '1 1 300px', minWidth: 0, display: 'grid', gap: 16 }}>
        {building && (
          <div className="site-panel" style={{ padding: 16, display: 'grid', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
              <h2 className="site-display" style={{ fontSize: 24 }}>Your army</h2>
              <span className="site-muted" style={{ fontSize: 14 }}>{total} pts</span>
            </div>
            <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 6 }}>
              {slots.map((s, i) => (
                <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 10,
                  ...(s.name ? { background: 'var(--bg)', border: '1px solid var(--panel-line)' } : current.includes(i) ? { border: '2px solid var(--accent)', background: '#3a3527' } : { border: '1px dashed var(--line)', color: 'var(--faint)' }) }}>
                  <span className="site-kicker" style={{ width: 64, flex: 'none', fontSize: 11 }}>{s.role}</span>
                  <span style={{ flex: 1, fontWeight: 600 }}>{s.name ?? (current.includes(i) ? 'Picking now…' : '—')}</span>
                  {s.name && <span className="site-muted" style={{ fontSize: 13 }}>Move {unitOf(s.name)!.move}</span>}
                </li>
              ))}
            </ol>
          </div>
        )}
        <div className="site-panel" style={{ padding: 16, display: 'grid', gap: 8 }}>
          <h2 className="site-display" style={{ fontSize: 24 }}>Everyone</h2>
          {L.players.map(p => (
            <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ flex: 1, fontWeight: 600 }}>{p.name}{p.bot ? ' (bot)' : ''}{p.id === L.me?.id ? ' (you)' : ''}</span>
              <span style={{ fontSize: 13, color: p.armyBuilt ? 'var(--ok)' : 'var(--warn)' }}>{p.armyBuilt ? `Done${p.army?.name ? `: ${p.army.name}` : ''}` : 'Building…'}</span>
              {L.me?.isHost && !p.armyBuilt && !p.bot && p.id !== L.me.id && (
                <button type="button" className="site-btn site-btn-small" style={{ minHeight: 30 }} title="If they can't get to it: build them an army from their own options"
                  onClick={() => { if (window.confirm(`Build ${p.name}'s army for them, with ${pickMode ? teamName(TEAM_KEYS[0]) : 'the first of each of their options'}?\n\nOnly do this if they can't build it themselves: if they're choosing right now, their picks are lost.`)) act('army', { playerId: p.id, army: pickMode ? teamArmy(TEAM_KEYS[0]) : botArmy(L.seed, p.id, L.settings.choices, p.name) }); }}>Build for them</button>
              )}
            </div>
          ))}
          <p className="site-muted site-small" style={{ marginTop: 4 }}>You see the other armies once everyone is done.</p>
        </div>
      </aside>
    </div>
  );
}
